import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsx mdx */

import DefaultLayout from "/Users/bradwestfall/Desktop/ReactTraining/Projects/monorepo/reacttraining.com/src/page-templates/BlogArticlePage.js";
export const _frontmatter = {};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h2 {...{
      "id": "tldr"
    }}><a parentName="h2" {...{
        "href": "#tldr",
        "aria-hidden": "true",
        "tabIndex": -1
      }}><span parentName="a" {...{
          "className": "icon icon-link"
        }}></span></a>{`tl;dr`}</h2>
    <ul>
      <li parentName="ul">{`We are bringing together the best of React Router and Reach Router into a new, hook-based API.`}</li>
      <li parentName="ul">{`React Router will be the surviving project.`}</li>
      <li parentName="ul">{`We'll introduce this API in a `}<em parentName="li">{`minor release`}</em>{` (5.x). That means it's 100% backward compatible. (Don't forget, we recently released React Router v5, it was a major version change only because of a dependency version range issue, there were no API changes.)`}</li>
      <li parentName="ul">{`We will continue to support @reach/router 1.0 with bugfixes`}</li>
    </ul>
    <h2 {...{
      "id": "background"
    }}><a parentName="h2" {...{
        "href": "#background",
        "aria-hidden": "true",
        "tabIndex": -1
      }}><span parentName="a" {...{
          "className": "icon icon-link"
        }}></span></a>{`Background`}</h2>
    <p>{`Five years ago Michael and I built the first version of React Router (!). When we released version 4 two years ago we said we wouldn't change anything about the API unless React itself fundamentally changed. Two years later and we haven't 😘. But now, with the introduction of hooks, React has fundamentally changed how we can compose state and behavior, and we want to take advantage of it.`}</p>
    <p>{`Taking these new tools (and things we've learned from starting fresh in @reach/router) we've created a smaller, smarter, and simpler routing utility. We think you'll love it.`}</p>
    <p>{`We don't have a release ready yet (we're still on our workshop tour) but we wanted to let everybody know what's coming, and what it means for you and your team.`}</p>
    <h2 {...{
      "id": "which-project-should-i-choose-today"
    }}><a parentName="h2" {...{
        "href": "#which-project-should-i-choose-today",
        "aria-hidden": "true",
        "tabIndex": -1
      }}><span parentName="a" {...{
          "className": "icon icon-link"
        }}></span></a>{`Which Project Should I Choose Today?`}</h2>
    <p>{`React Router will offer an incremental migration path, but @reach/router looks slightly more like the new API. So:`}</p>
    <ul>
      <li parentName="ul">
        <p parentName="li">{`If you only have a few routes you may want to pick @reach/router. The @reach/router API looks slightly more like the new API so it'll be easier to migrate in one commit. It shouldn't take more than an afternoon.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`If you have a lot of routes, especially with a lot of nested `}<code parentName="p" {...{
            "className": "language-text"
          }}>{`Route`}</code>{` and `}<code parentName="p" {...{
            "className": "language-text"
          }}>{`Switch`}</code>{` components, you may want to pick React Router. It will be 100% backward compatible, offering an incremental migration path to the new hook-based APIs.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`The current APIs of React Router and @reach/router will be supported for the foreseeable future with bugfixes (just like how we have quietly maintained React Router v3 for the last few years). So, if the new stuff isn't compelling, you won't need to update anything.`}</p>
      </li>
    </ul>
    <h3 {...{
      "id": "im-using-reachrouter-now-what"
    }}><a parentName="h3" {...{
        "href": "#im-using-reachrouter-now-what",
        "aria-hidden": "true",
        "tabIndex": -1
      }}><span parentName="a" {...{
          "className": "icon icon-link"
        }}></span></a>{`I'm Using @reach/router, Now What?`}</h3>
    <p>{`We will continue to support @reach/router 1.0 with bugfixes for the foreseeable future, so you don't need to do anything for a long time if you don't want to.`}</p>
    <p>{`If the new API interests you however, the migration should be pretty straightforward. The next React Router API, on the surface, looks more like @reach/router than React Router. Most of the changes have a one-to-one mapping between both APIs and some are identical.`}</p>
    <p>{`Because of this, we `}<em parentName="p">{`don't`}</em>{` recommend switching to React Router in preparation for the hook-based API. You'll have an easier time migrating from your current code.`}</p>
    <h3 {...{
      "id": "im-using-react-router-now-what"
    }}><a parentName="h3" {...{
        "href": "#im-using-react-router-now-what",
        "aria-hidden": "true",
        "tabIndex": -1
      }}><span parentName="a" {...{
          "className": "icon icon-link"
        }}></span></a>{`I'm Using React Router, Now What?`}</h3>
    <p>{`The next API is backward compatible and opt-in. We will continue to maintain the v5 API for the foreseeable future, so you don't have to do anything for a long time if you don't want to.`}</p>
    <p>{`If you would like to take advantage of the new API, you will be able to migrate incrementally. It'll work something like this:`}</p>
    <ol>
      <li parentName="ol">{`Pick one of your deepest `}<code parentName="li" {...{
          "className": "language-text"
        }}>{`Route`}</code>{` or `}<code parentName="li" {...{
          "className": "language-text"
        }}>{`Switch`}</code>{` components`}</li>
      <li parentName="ol">{`Update the API`}</li>
    </ol>
    <p>{`The migrated section of the app now has access to the new APIs while the rest of the app will continue to work as it did before, without any changes. Continue this until the whole app is migrated--or don't. You can start new features of your app with new features of React Router without having to change any of your other routing code.`}</p>
    <h2 {...{
      "id": "features"
    }}><a parentName="h2" {...{
        "href": "#features",
        "aria-hidden": "true",
        "tabIndex": -1
      }}><span parentName="a" {...{
          "className": "icon icon-link"
        }}></span></a>{`Features`}</h2>
    <p>{`Here's a run-down of a few of the features we're excited about:`}</p>
    <ul>
      <li parentName="ul">{`Smaller bundle. Current size of the Router dependency in a fully migrated app is around 3kb`}</li>
      <li parentName="ul">{`Built of composable parts. Simple apps don't need everything.`}</li>
      <li parentName="ul">{`Smart path matching, no more messing around with the order of your routes or `}<code parentName="li" {...{
          "className": "language-text"
        }}>{`exact`}</code>{` props`}</li>
      <li parentName="ul">{`Nested Route Configs (much like v3 and @reach/router)`}</li>
      <li parentName="ul">{`Relative Links, navigation, and routes`}</li>
      <li parentName="ul">{`Access route data with `}<code parentName="li" {...{
          "className": "language-text"
        }}>{`useParams()`}</code>{` from anywhere below the route`}</li>
      <li parentName="ul">{`Access to `}<code parentName="li" {...{
          "className": "language-text"
        }}>{`location`}</code>{` and `}<code parentName="li" {...{
          "className": "language-text"
        }}>{`navigate`}</code>{` with `}<code parentName="li" {...{
          "className": "language-text"
        }}>{`useLocation()`}</code>{` anywhere in the app.`}</li>
      <li parentName="ul">{`Route parameter validation`}</li>
      <li parentName="ul">{`Route matching on location state`}</li>
      <li parentName="ul">{`Automatic focus management on route transitions`}</li>
      <li parentName="ul">{`Concurrent/Suspense Ready`}</li>
    </ul>
    <h3 {...{
      "id": "code-samples"
    }}><a parentName="h3" {...{
        "href": "#code-samples",
        "aria-hidden": "true",
        "tabIndex": -1
      }}><span parentName="a" {...{
          "className": "icon icon-link"
        }}></span></a>{`Code Samples`}</h3>
    <p>{`Here's a sneak peek at some of the new API.`}</p>
    <div {...{
      "className": "gatsby-highlight",
      "data-language": "jsx"
    }}><pre parentName="div" {...{
        "className": "language-jsx"
      }}><code parentName="pre" {...{
          "className": "language-jsx"
        }}><span parentName="code" {...{
            "className": "token comment"
          }}>{`// with relative links you can change the query string easily`}</span>{`
`}<span parentName="code" {...{
            "className": "token comment"
          }}>{`// without having to reconstruct the current URL`}</span>{`
`}<span parentName="code" {...{
            "className": "token tag"
          }}><span parentName="span" {...{
              "className": "token tag"
            }}><span parentName="span" {...{
                "className": "token punctuation"
              }}>{`<`}</span><span parentName="span" {...{
                "className": "token class-name"
              }}>{`Link`}</span></span>{` `}<span parentName="span" {...{
              "className": "token attr-name"
            }}>{`to`}</span><span parentName="span" {...{
              "className": "token attr-value"
            }}><span parentName="span" {...{
                "className": "token punctuation"
              }}>{`=`}</span><span parentName="span" {...{
                "className": "token punctuation"
              }}>{`"`}</span>{`?something=different`}<span parentName="span" {...{
                "className": "token punctuation"
              }}>{`"`}</span></span><span parentName="span" {...{
              "className": "token punctuation"
            }}>{`/>`}</span></span>{`

`}<span parentName="code" {...{
            "className": "token comment"
          }}>{`// Or link deeper, assuming you're at \`/users/123\` you can link`}</span>{`
`}<span parentName="code" {...{
            "className": "token comment"
          }}>{`// to \`/users/123/account/reset-password\` with:`}</span>{`
`}<span parentName="code" {...{
            "className": "token tag"
          }}><span parentName="span" {...{
              "className": "token tag"
            }}><span parentName="span" {...{
                "className": "token punctuation"
              }}>{`<`}</span><span parentName="span" {...{
                "className": "token class-name"
              }}>{`Link`}</span></span>{` `}<span parentName="span" {...{
              "className": "token attr-name"
            }}>{`to`}</span><span parentName="span" {...{
              "className": "token attr-value"
            }}><span parentName="span" {...{
                "className": "token punctuation"
              }}>{`=`}</span><span parentName="span" {...{
                "className": "token punctuation"
              }}>{`"`}</span>{`account/reset-password`}<span parentName="span" {...{
                "className": "token punctuation"
              }}>{`"`}</span></span>{` `}<span parentName="span" {...{
              "className": "token punctuation"
            }}>{`/>`}</span></span>{`

`}<span parentName="code" {...{
            "className": "token comment"
          }}>{`// useLocation makes navigation-based side-effects a breeze`}</span>{`
`}<span parentName="code" {...{
            "className": "token keyword"
          }}>{`function`}</span>{` `}<span parentName="code" {...{
            "className": "token function"
          }}>{`usePageViews`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`(`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`)`}</span>{` `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`{`}</span>{`
  `}<span parentName="code" {...{
            "className": "token keyword"
          }}>{`const`}</span>{` `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`{`}</span>{` location `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`}`}</span>{` `}<span parentName="code" {...{
            "className": "token operator"
          }}>{`=`}</span>{` `}<span parentName="code" {...{
            "className": "token function"
          }}>{`useLocation`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`(`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`)`}</span>{`
  `}<span parentName="code" {...{
            "className": "token function"
          }}>{`useEffect`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`(`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`(`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`)`}</span>{` `}<span parentName="code" {...{
            "className": "token operator"
          }}>{`=>`}</span>{` `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`{`}</span>{`
    `}<span parentName="code" {...{
            "className": "token function"
          }}>{`ga`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`(`}</span><span parentName="code" {...{
            "className": "token string"
          }}>{`'send'`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`,`}</span>{` `}<span parentName="code" {...{
            "className": "token string"
          }}>{`'pageview'`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`,`}</span>{` location`}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`.`}</span>{`pathname`}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`)`}</span>{`
  `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`}`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`,`}</span>{` `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`[`}</span>{`location`}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`]`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`)`}</span>{`
`}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`}`}</span>{`

`}<span parentName="code" {...{
            "className": "token comment"
          }}>{`// You can access params with a hook`}</span>{`
`}<span parentName="code" {...{
            "className": "token comment"
          }}>{`// (much friendlier for TypeScript, too)`}</span>{`
`}<span parentName="code" {...{
            "className": "token keyword"
          }}>{`function`}</span>{` `}<span parentName="code" {...{
            "className": "token function"
          }}>{`SomeComponent`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`(`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`)`}</span>{` `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`{`}</span>{`
  `}<span parentName="code" {...{
            "className": "token keyword"
          }}>{`const`}</span>{` `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`{`}</span>{` userId `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`}`}</span>{` `}<span parentName="code" {...{
            "className": "token operator"
          }}>{`=`}</span>{` `}<span parentName="code" {...{
            "className": "token function"
          }}>{`useParams`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`(`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`)`}</span>{`
  `}<span parentName="code" {...{
            "className": "token comment"
          }}>{`// ...`}</span>{`
`}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`}`}</span>{`

`}<span parentName="code" {...{
            "className": "token comment"
          }}>{`// With the new route ranking algorithm, we're able to validate routes`}</span>{`
`}<span parentName="code" {...{
            "className": "token comment"
          }}>{`// as they are matching the location. If \`validate\` returns false, the`}</span>{`
`}<span parentName="code" {...{
            "className": "token comment"
          }}>{`// route acts as if the path didn't match the location at all`}</span>{`
`}<span parentName="code" {...{
            "className": "token tag"
          }}><span parentName="span" {...{
              "className": "token tag"
            }}><span parentName="span" {...{
                "className": "token punctuation"
              }}>{`<`}</span><span parentName="span" {...{
                "className": "token class-name"
              }}>{`Route`}</span></span>{`
  `}<span parentName="span" {...{
              "className": "token attr-name"
            }}>{`path`}</span><span parentName="span" {...{
              "className": "token attr-value"
            }}><span parentName="span" {...{
                "className": "token punctuation"
              }}>{`=`}</span><span parentName="span" {...{
                "className": "token punctuation"
              }}>{`"`}</span>{`event/:date`}<span parentName="span" {...{
                "className": "token punctuation"
              }}>{`"`}</span></span>{`
  `}<span parentName="span" {...{
              "className": "token attr-name"
            }}>{`validate`}</span><span parentName="span" {...{
              "className": "token script language-javascript"
            }}><span parentName="span" {...{
                "className": "token script-punctuation punctuation"
              }}>{`=`}</span><span parentName="span" {...{
                "className": "token punctuation"
              }}>{`{`}</span><span parentName="span" {...{
                "className": "token parameter"
              }}>{`params`}</span>{` `}<span parentName="span" {...{
                "className": "token operator"
              }}>{`=>`}</span>{` `}<span parentName="span" {...{
                "className": "token function"
              }}>{`isValidDate`}</span><span parentName="span" {...{
                "className": "token punctuation"
              }}>{`(`}</span>{`params`}<span parentName="span" {...{
                "className": "token punctuation"
              }}>{`.`}</span>{`date`}<span parentName="span" {...{
                "className": "token punctuation"
              }}>{`)`}</span><span parentName="span" {...{
                "className": "token punctuation"
              }}>{`}`}</span></span>{`
`}<span parentName="span" {...{
              "className": "token punctuation"
            }}>{`>`}</span></span><span parentName="code" {...{
            "className": "token plain-text"
          }}>{`
  `}</span><span parentName="code" {...{
            "className": "token tag"
          }}><span parentName="span" {...{
              "className": "token tag"
            }}><span parentName="span" {...{
                "className": "token punctuation"
              }}>{`<`}</span><span parentName="span" {...{
                "className": "token class-name"
              }}>{`Event`}</span></span><span parentName="span" {...{
              "className": "token punctuation"
            }}>{`/>`}</span></span><span parentName="code" {...{
            "className": "token plain-text"
          }}>{`
`}</span><span parentName="code" {...{
            "className": "token tag"
          }}><span parentName="span" {...{
              "className": "token tag"
            }}><span parentName="span" {...{
                "className": "token punctuation"
              }}>{`</`}</span><span parentName="span" {...{
                "className": "token class-name"
              }}>{`Route`}</span></span><span parentName="span" {...{
              "className": "token punctuation"
            }}>{`>`}</span></span></code></pre></div>
    <h2 {...{
      "id": "timeline"
    }}><a parentName="h2" {...{
        "href": "#timeline",
        "aria-hidden": "true",
        "tabIndex": -1
      }}><span parentName="a" {...{
          "className": "icon icon-link"
        }}></span></a>{`Timeline`}</h2>
    <p>{`We will be updating the `}<a parentName="p" {...{
        "href": "https://github.com/ReactTraining/react-router/issues/6885"
      }}>{`5.x Roadmap`}</a>{` with these new items and get some estimated release dates from there.`}</p>
    <p>{`Thanks!`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      